import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFontSize } from '../../contexts/FontContext';
import { useTranslation } from '../../translations';
import bluelogo from '../../assets/bluelogo.png';
import {
  HomeIcon,
  ViewColumnsIcon,
  FolderIcon,
  ChartBarIcon,
  UserGroupIcon,
  PlusIcon,
  ChatBubbleLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  NoSymbolIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  CogIcon,
  DocumentTextIcon,
  DocumentPlusIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  ListBulletIcon,
  PhoneIcon,
  ChartPieIcon,
  EnvelopeIcon,
  TableCellsIcon,
  BookOpenIcon,
  KeyIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  MicrophoneIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  ShieldExclamationIcon,
  BeakerIcon,
  DocumentArrowDownIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const Sidebar = ({ onCreateTicket, isCollapsed, setIsCollapsed, isMobileOpen = false, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projects, selectedProject, selectProject, reloadProjects } = useProject();
  const { logout, isAdmin, hasModuleAccess, canAccessWorkReport } = useAuth();
  const { language } = useLanguage();
  const { getFontClass } = useFontSize();
  const { t } = useTranslation(language);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [openSubMenus, setOpenSubMenus] = useState({});

  const menuItems = [
    {
      label: 'Dashboard',
      icon: ChartBarIcon,
      path: '/dashboard',
      description: language === 'tr' ? 'Ana sayfa ve özetler' : 'Dashboard and summaries'
    },
    {
      label: t('board'),
      icon: ViewColumnsIcon,
      path: '/board',
      description: language === 'tr' ? 'Kanban board ve proje yönetimi' : 'Kanban board and project management'
    },
    {
      label: t('tickets'),
      icon: ChatBubbleLeftIcon,
      path: '/tickets',
      description: language === 'tr' ? 'Ticket listesi ve yönetimi' : 'Ticket list and management'
    },
    {
      label: language === 'tr' ? 'Mail (M365)' : 'Mail (M365)',
      icon: EnvelopeIcon,
      path: '/mail/summary',
      description:
        language === 'tr'
          ? 'Microsoft 365 e-posta özeti ve gelen kutusu'
          : 'Microsoft 365 mail summary and inbox'
    }
  ];

  const stopSaleMenuItems = [
    {
      label: language === 'tr' ? 'Tanımlamalar' : 'Definitions',
      icon: CogIcon,
      path: '/marketing-budget/definitions',
      description: language === 'tr' ? 'Genel tanımlamalar' : 'General definitions',
      subItems: [
        {
          label: language === 'tr' ? 'Bütçe Tanımları' : 'Budget Definitions',
          icon: CogIcon,
          path: '/marketing-budget/definitions',
          description: language === 'tr' ? 'Bütçe tanım yönetimi' : 'Budget definition management'
        }
      ]
    },
    {
      label: language === 'tr' ? 'Open & Stop Sale' : 'Open & Stop Sale',
      icon: NoSymbolIcon,
      path: '/stopsale/dashboard',
      description: language === 'tr' ? 'Oda satış yönetimi' : 'Room sales management',
      subItems: [
        {
          label: language === 'tr' ? 'Calendar' : 'Calendar',
          icon: CalendarIcon,
          path: '/stopsale/calendar',
          description: language === 'tr' ? 'Stop sale takvim görünümü' : 'Stop sale calendar view'
        },
        {
          label: language === 'tr' ? 'Mail alıcıları' : 'Mail recipients',
          icon: EnvelopeIcon,
          path: '/stopsale/mail-recipients',
          description:
            language === 'tr'
              ? 'Stop sale bildirim e-postası alacak kişiler'
              : 'Who receives stop sale notification emails'
        }
      ]
    },
    {
      label: language === 'tr' ? 'Actions' : 'Actions',
      icon: ClipboardDocumentListIcon,
      path: '/stopsale/actions',
      description: language === 'tr' ? 'İşlemler ve aksiyonlar' : 'Actions and operations'
    },
    {
      label: language === 'tr' ? 'Marketing Budget' : 'Marketing Budget',
      icon: CurrencyDollarIcon,
      path: '/marketing-budget/dashboard',
      description: language === 'tr' ? 'Marketing bütçe yönetimi' : 'Marketing budget management',
      subItems: [
        {
          label: language === 'tr' ? 'Budget' : 'Budget',
          icon: ChartBarIcon,
          path: '/marketing-budget/budget',
          description: language === 'tr' ? 'Bütçe girişi ve yönetimi' : 'Budget entry and management'
        },
        {
          label: language === 'tr' ? 'Basic Budget' : 'Basic Budget',
          icon: DocumentPlusIcon,
          path: '/marketing-budget/basic-budget',
          description: language === 'tr' ? 'Basit bütçe girişi' : 'Simple budget entry'
        },
        {
          label: language === 'tr' ? 'Percent Budget' : 'Percent Budget',
          icon: ChartBarIcon,
          path: '/marketing-budget/percent-budget',
          description: language === 'tr' ? 'Bütçe yüzde yönetimi' : 'Budget percentage management'
        },
        {
          label: language === 'tr' ? 'Reports' : 'Reports',
          icon: DocumentChartBarIcon,
          path: '/marketing-budget/reports',
          description: language === 'tr' ? 'Bütçe raporları' : 'Budget reports'
        },
        {
          label: language === 'tr' ? 'Airport Tourist Data' : 'Airport Tourist Data',
          icon: ChartBarIcon,
          path: '/marketing-budget/airport-tourist-data',
          description: language === 'tr' ? 'Havalimanı turist verileri' : 'Airport tourist data'
        }
      ]
    },
    {
      label: language === 'tr' ? 'B2B Meetings & Activities' : 'B2B Meetings & Activities',
      icon: PhoneIcon,
      path: '/sales-call',
      description: language === 'tr' ? 'Satış görüşmeleri yönetimi' : 'Sales call management',
      subItems: [
        {
          label: language === 'tr' ? 'Dashboard' : 'Dashboard',
          icon: ChartBarIcon,
          path: '/sales-call/dashboard',
          description: language === 'tr' ? 'Satış görüşmeleri özeti' : 'Sales call summary'
        },
        {
          label: language === 'tr' ? 'B2B Meetings & Activities' : 'B2B Meetings & Activities',
          icon: PhoneIcon,
          path: '/sales-call',
          description: language === 'tr' ? 'Satış görüşmeleri' : 'Sales calls'
        },
        {
          label: language === 'tr' ? 'Road Planner Monthly' : 'Road Planner Monthly',
          icon: CalendarIcon,
          path: '/sales-call/road-planner-monthly',
          description: language === 'tr' ? 'Aylık planlayıcı' : 'Monthly planner'
        },
        {
          label: language === 'tr' ? 'Sales Call Visit Report' : 'Sales Call Visit Report',
          icon: ClipboardDocumentCheckIcon,
          path: '/sales-call/visit-report',
          description: language === 'tr' ? 'Ziyaret Raporu' : 'Visit Report'
        },
        {
          label: language === 'tr' ? 'Travel Expenses' : 'Travel Expenses',
          icon: CurrencyDollarIcon,
          path: '/sales-call/expenses',
          description: language === 'tr' ? 'Harcırah takibi' : 'Travel expenses tracking'
        },
        {
          label: language === 'tr' ? 'Mail Schedule Settings' : 'Mail Schedule Settings',
          icon: EnvelopeIcon,
          path: '/sales-call/mail-schedules',
          description: language === 'tr' ? 'Mail alıcı ve saat planları' : 'Mail recipient and schedule settings'
        },
        {
          label: language === 'tr' ? 'AI Insights' : 'AI Insights',
          icon: ChartPieIcon,
          path: '/sales-call/ai-insights',
          description: language === 'tr' ? 'Yapay zeka destekli analizler' : 'AI-powered analytics'
        },
        {
          label: language === 'tr' ? 'Detailed Insights' : 'Detailed Insights',
          icon: TableCellsIcon,
          path: '/sales-call/record-analyses',
          description: language === 'tr' ? 'Detaylı görüşme analizleri' : 'Detailed call analyses'
        },
        {
          label: language === 'tr' ? 'Addresses' : 'Addresses',
          icon: BookOpenIcon,
          path: '/sales-call/address-book',
          description: language === 'tr' ? 'Adres defteri' : 'Address book'
        },
        {
          label: language === 'tr' ? 'Sub Agencies' : 'Sub Agencies',
          icon: UserGroupIcon,
          path: '/sales-call/sub-agencies',
          description: language === 'tr' ? 'Alt acente yönetimi' : 'Sub agency management'
        }
      ]
    },
    {
      label: language === 'tr' ? 'Content Management' : 'Content Management',
      icon: DocumentTextIcon,
      path: '/content-management',
      description: language === 'tr' ? 'Blog yazıları ve içerik yönetimi' : 'Blog posts and content management',
      subItems: [
        {
          label: language === 'tr' ? 'Blog Yazıları' : 'Blog Posts',
          icon: DocumentTextIcon,
          path: '/content-management',
          description: language === 'tr' ? 'Blog yazılarını yönet' : 'Manage blog posts'
        },
        {
          label: language === 'tr' ? 'Rules' : 'Rules',
          icon: CogIcon,
          path: '/content-management/rules',
          description: language === 'tr' ? 'Blog yazısı kuralları' : 'Blog writing rules'
        }
      ]
    },
    {
      label: language === 'tr' ? 'Marketing Management' : 'Marketing Management',
      icon: ChartBarIcon,
      path: '/marketing-management', // Path eklendi
      description: language === 'tr' ? 'Marketing yönetimi' : 'Marketing management',
      subItems: [
        {
          label: language === 'tr' ? 'Mailing' : 'Mailing',
          icon: EnvelopeIcon,
          path: '/marketing-management/mailing-planner',
          description: language === 'tr' ? 'Mailing menüsü' : 'Mailing menu',
          subItems: [
            {
              label: language === 'tr' ? 'Mailing Planner' : 'Mailing Planner',
              icon: EnvelopeIcon,
              path: '/marketing-management/mailing-planner',
              description: language === 'tr' ? 'E-posta planlayıcı' : 'Email planner'
            },
            {
              label: language === 'tr' ? 'Monthly Mailing Calendar' : 'Monthly Mailing Calendar',
              icon: CalendarIcon,
              path: '/sales-call/calendar',
              description: language === 'tr' ? 'Aylık mailing takvimi' : 'Monthly mailing calendar'
            },
            {
              label: language === 'tr' ? 'Mailing Calendar' : 'Mailing Calendar',
              icon: CalendarIcon,
              path: '/marketing-management/mailing-calendar',
              description: language === 'tr' ? 'E-posta takvimi' : 'Email calendar'
            }
          ]
        },
        {
          label: language === 'tr' ? 'Content' : 'Content',
          icon: DocumentTextIcon,
          path: '/marketing-management/content-planner',
          description: language === 'tr' ? 'Content menüsü' : 'Content menu',
          subItems: [
            {
              label: language === 'tr' ? 'Content Planner' : 'Content Planner',
              icon: DocumentTextIcon,
              path: '/marketing-management/content-planner',
              description: language === 'tr' ? 'İçerik planlayıcı' : 'Content planner'
            },
            {
              label: language === 'tr' ? 'Content Calendar' : 'Content Calendar',
              icon: CalendarIcon,
              path: '/marketing-management/content-calendar',
              description: language === 'tr' ? 'İçerik takvimi' : 'Content calendar'
            },
            {
              label: language === 'tr' ? 'Content Presentation Table' : 'Content Presentation Table',
              icon: TableCellsIcon,
              path: '/marketing-management/content-presentation',
              description: language === 'tr' ? 'Yönetim sunumu tablo görünümü' : 'Presentation table view'
            }
          ]
        }
      ]
    },
    {
      label: language === 'tr' ? 'Anket Yönetimi' : 'Survey Management',
      icon: ClipboardDocumentListIcon,
      path: '/survey',
      description: language === 'tr' ? 'Anket ve form yönetimi' : 'Survey and form management',
      subItems: [
        {
          label: language === 'tr' ? 'Dashboard' : 'Dashboard',
          icon: ChartBarIcon,
          path: '/survey/dashboard',
          description: language === 'tr' ? 'Anket genel bakış' : 'Survey overview'
        },
        {
          label: language === 'tr' ? 'Anketler' : 'Surveys',
          icon: ListBulletIcon,
          path: '/survey/list',
          description: language === 'tr' ? 'Anket listesi' : 'Survey list'
        },
        {
          label: language === 'tr' ? 'Yanıtlar' : 'Submissions',
          icon: ClipboardDocumentCheckIcon,
          path: '/survey/submissions/all',
          description: language === 'tr' ? 'Anket yanıtları' : 'Survey submissions'
        },
        {
          label: language === 'tr' ? 'Analizler' : 'Analytics',
          icon: ChartPieIcon,
          path: '/survey/analytics/all',
          description: language === 'tr' ? 'Anket analizleri' : 'Survey analytics'
        },
        {
          label: language === 'tr' ? 'Anket Oluştur' : 'Create Survey',
          icon: PlusIcon,
          path: '/survey/create',
          description: language === 'tr' ? 'Yeni anket oluştur' : 'Create new survey'
        }
      ]
    }
  ];

  const qualityMenuItems = [
    {
      label: 'Kalite Yönetimi',
      icon: ClipboardDocumentCheckIcon,
      path: '/quality-management/doif-dashboard',
      description: 'DÖİF ve kalite yönetimi',
      subItems: [
        {
          label: 'DOIF Dashboard',
          icon: ChartBarIcon,
          path: '/quality-management/doif-dashboard',
          description: 'Özet istatistik ve raporlama panosu'
        },
        {
          label: 'DOIF Aşamaları',
          icon: ClipboardDocumentCheckIcon,
          path: '/quality-management/df-asamalar',
          description: 'DOIF iş akışı aşamaları',
          subItems: [
            {
              label: 'DOIF Başlat',
              icon: ClipboardDocumentCheckIcon,
              path: '/quality-management/df-baslat',
              description: 'Başlatma aşamasındaki formlar'
            },
            {
              label: 'DOIF İşlemleri',
              icon: ClipboardDocumentCheckIcon,
              path: '/quality-management/df-islemleri',
              description: 'Aksiyon aşaması'
            },
            {
              label: 'DOIF Onay',
              icon: ClipboardDocumentCheckIcon,
              path: '/quality-management/df-onay',
              description: 'Onay aşaması'
            },
            {
              label: 'DOIF Kapatma',
              icon: ClipboardDocumentCheckIcon,
              path: '/quality-management/df-kapatma',
              description: 'Kapatma aşaması'
            },
          ]
        },
        {
          label: 'Kanban Pano',
          icon: ViewColumnsIcon,
          path: '/quality-management/board',
          description: 'Duruma göre pano görünümü'
        },
        {
          label: 'Tanımlar',
          icon: DocumentTextIcon,
          path: '/quality-management/definitions/hotels',
          description: 'Ana tanımlar',
          subItems: [
            {
              label: 'Oteller',
              icon: HomeIcon,
              path: '/quality-management/definitions/hotels',
              description: 'Otel tanımları'
            },
            {
              label: 'Departmanlar',
              icon: UserGroupIcon,
              path: '/quality-management/definitions/departments',
              description: 'Departman tanımları'
            },
            {
              label: 'Kullanıcılar',
              icon: UserGroupIcon,
              path: '/quality-management/definitions/users',
              description: 'Kullanıcı tanımları'
            },
            {
              label: 'DÖİF Tanımları',
              icon: DocumentTextIcon,
              path: '/quality-management/definitions/df-definitions',
              description: 'DÖİF tanım listesi'
            },
            {
              label: 'Uygunsuzluk Özetleri',
              icon: ListBulletIcon,
              path: '/quality-management/definitions/nonconformity-summaries',
              description: 'Uygunsuzluk özeti tanımları'
            },
            {
              label: 'Uygunsuzluk Kaynakları',
              icon: ClipboardDocumentListIcon,
              path: '/quality-management/definitions/nonconformity-sources',
              description: 'Uygunsuzluk kaynağı tanımları'
            },
            {
              label: 'Kök Neden Özetleri',
              icon: QuestionMarkCircleIcon,
              path: '/quality-management/definitions/root-cause-summaries',
              description: 'Kök neden özeti tanımları'
            },
            ...(isAdmin() ? [{
              label: 'DÖİF görünürlük yönetimi',
              icon: UserGroupIcon,
              path: '/quality-management/definitions/dof-access',
              description: 'Tüm DÖİF görüntüleme yetkisi'
            }] : []),
          ]
        }
      ]
    }
  ];

  const generalMenuItems = [
    {
      label: language === 'tr' ? 'Lisans Yönetimi' : 'License Management',
      icon: KeyIcon,
      path: '/general/licenses',
      description: language === 'tr' ? 'Lisans dashboard ve özet' : 'License dashboard and overview',
      subItems: [
        {
          label: language === 'tr' ? 'Dashboard' : 'Dashboard',
          icon: ChartBarIcon,
          path: '/general/licenses',
          description: language === 'tr' ? 'Lisans özeti' : 'License overview'
        },
        {
          label: language === 'tr' ? 'Lisans Listesi' : 'License List',
          icon: ListBulletIcon,
          path: '/general/licenses/list',
          description: language === 'tr' ? 'Tüm lisanslar' : 'All licenses'
        },
        {
          label: language === 'tr' ? 'Raporlar' : 'Reports',
          icon: DocumentChartBarIcon,
          path: '/general/licenses/reports',
          description: language === 'tr' ? 'Lisans raporları' : 'License reports'
        },
        {
          label: language === 'tr' ? 'Tanımlamalar' : 'Definitions',
          icon: CogIcon,
          path: '/general/licenses/definitions',
          description: language === 'tr' ? 'Lisans tipleri' : 'License types'
        }
      ]
    },
    {
      label: language === 'tr' ? 'QR Kod' : 'QR Code',
      icon: QrCodeIcon,
      path: '/general/qr',
      description: language === 'tr' ? 'Link ve özel sayfa QR kodları' : 'QR codes for links and custom pages',
      subItems: [
        {
          label: language === 'tr' ? 'Link QR' : 'Link QR',
          icon: QrCodeIcon,
          path: '/general/qr',
          description: language === 'tr' ? 'Harici link için QR' : 'QR for external link'
        },
        {
          label: language === 'tr' ? 'Özel Sayfalar' : 'Custom Pages',
          icon: QrCodeIcon,
          path: '/general/qr/custom-pages',
          description: language === 'tr' ? 'qr.akkahotels.com/slug sayfaları' : 'Custom pages at qr.akkahotels.com/slug'
        }
      ]
    }
  ];

  const vehicleMaintenanceMenuItems = [
    {
      label: language === 'tr' ? 'Araç Bakım' : 'Vehicle Maintenance',
      icon: TruckIcon,
      path: '/vehicle-maintenance/vehicles',
      description: language === 'tr' ? 'Araç takip, bakım, muayene ve sigorta' : 'Vehicle tracking, maintenance, inspection and insurance',
      subItems: [
        {
          label: language === 'tr' ? 'Araçlar' : 'Vehicles',
          icon: ListBulletIcon,
          path: '/vehicle-maintenance/vehicles',
          description: language === 'tr' ? 'Araç master kayıtları' : 'Vehicle master records',
        },
        {
          label: language === 'tr' ? 'TÜVTÜRK - Egzoz' : 'Inspections',
          icon: ClipboardDocumentCheckIcon,
          path: '/vehicle-maintenance/inspections',
          description: language === 'tr' ? 'Muayene ve egzoz takip' : 'Inspection and exhaust tracking',
        },
        {
          label: language === 'tr' ? 'Trafik - Kasko' : 'Insurance',
          icon: DocumentTextIcon,
          path: '/vehicle-maintenance/insurance',
          description: language === 'tr' ? 'Trafik ve kasko takip' : 'Traffic and kasko tracking',
        },
        {
          label: language === 'tr' ? 'Bakım' : 'Maintenance',
          icon: CogIcon,
          path: '/vehicle-maintenance/maintenance',
          description: language === 'tr' ? 'Araç bakım takip' : 'Vehicle maintenance tracking',
        },
        {
          label: language === 'tr' ? 'KM Takip' : 'Mileage',
          icon: TableCellsIcon,
          path: '/vehicle-maintenance/mileage',
          description: language === 'tr' ? 'Haftalık kilometre takip' : 'Weekly mileage tracking',
        },
        {
          label: language === 'tr' ? 'Trafik Cezaları' : 'Traffic Fines',
          icon: ExclamationTriangleIcon,
          path: '/vehicle-maintenance/traffic-fines',
          description: language === 'tr' ? 'Trafik cezası takip ve tahsilat' : 'Traffic fine tracking and collection',
        },
        {
          label: language === 'tr' ? 'Arıza / Hasar' : 'Breakdown / Damage',
          icon: WrenchScrewdriverIcon,
          path: '/vehicle-maintenance/breakdowns',
          description: language === 'tr' ? 'Arıza ve hasar takip' : 'Breakdown and damage tracking',
        },
        {
          label: language === 'tr' ? 'Kaza Takip' : 'Accident Tracking',
          icon: ShieldExclamationIcon,
          path: '/vehicle-maintenance/accidents',
          description: language === 'tr' ? 'Kaza tutanağı ve fotoğraf takip' : 'Accident report and photo tracking',
        },
        ...(isAdmin() ? [
          {
            label: language === 'tr' ? 'Denetim Logu' : 'Audit Log',
            icon: ClipboardDocumentListIcon,
            path: '/vehicle-maintenance/audit-logs',
            description: language === 'tr' ? 'Kim neyi ne zaman değiştirdi' : 'Who changed what and when',
          },
          {
            label: language === 'tr' ? 'Erişim Yönetimi' : 'Access Management',
            icon: UserGroupIcon,
            path: '/vehicle-maintenance/access',
            description: language === 'tr' ? 'Araç bakım modül yetkileri' : 'Vehicle maintenance module permissions',
          },
        ] : []),
      ],
    },
  ];

  const camasirhaneMenuItems = [
    {
      label: language === 'tr' ? 'Genel Bakış' : 'Overview',
      icon: ChartPieIcon,
      subItems: [
        {
          label: language === 'tr' ? 'Pano' : 'Dashboard',
          icon: ChartPieIcon,
          path: '/camasirhane/dashboard',
          description: language === 'tr' ? 'Özet gösterge paneli' : 'Summary dashboard',
        },
        {
          label: language === 'tr' ? 'Genel Toplam' : 'Summary',
          icon: ChartBarIcon,
          path: '/camasirhane/summary',
          description: language === 'tr' ? 'Dönem özet raporu' : 'Period summary report',
        },
      ],
    },
    {
      label: language === 'tr' ? 'Veri Girişi' : 'Data Entry',
      icon: ClipboardDocumentListIcon,
      subItems: [
        {
          label: language === 'tr' ? 'Kirli / Temiz Tartım' : 'Weighing',
          icon: TableCellsIcon,
          path: '/camasirhane/weighing',
          description: language === 'tr' ? 'Günlük tartım kayıtları' : 'Daily weighing records',
        },
        {
          label: language === 'tr' ? 'Üretim Adet' : 'Production',
          icon: ClipboardDocumentListIcon,
          path: '/camasirhane/production',
          description: language === 'tr' ? 'Günlük üretim adetleri' : 'Daily production quantities',
        },
        {
          label: language === 'tr' ? 'Maliyet' : 'Cost',
          icon: CurrencyDollarIcon,
          path: '/camasirhane/cost',
          description: language === 'tr' ? 'Aylık maliyet ve geceleme' : 'Monthly cost and occupancy',
        },
        {
          label: language === 'tr' ? 'Demir Ölçüm' : 'Iron Measurement',
          icon: BeakerIcon,
          path: '/camasirhane/iron',
          description: language === 'tr' ? 'Demir ve su sertliği takibi' : 'Iron and water hardness tracking',
        },
      ],
    },
    {
      label: language === 'tr' ? 'Araçlar' : 'Tools',
      icon: DocumentArrowDownIcon,
      subItems: [
        {
          label: language === 'tr' ? 'Excel Aktarım' : 'Excel Import',
          icon: DocumentArrowDownIcon,
          path: '/camasirhane/import',
          description: language === 'tr' ? 'Excel içe/dışa aktarım' : 'Excel import and export',
        },
      ],
    },
    {
      label: language === 'tr' ? 'Yönetim' : 'Administration',
      icon: CogIcon,
      subItems: [
        {
          label: language === 'tr' ? 'Genel' : 'General',
          icon: CogIcon,
          path: '/camasirhane/admin',
          description: language === 'tr' ? 'Personel, dönem ve audit' : 'Staff, periods and audit',
        },
        ...(isAdmin() ? [
          {
            label: language === 'tr' ? 'Tanımlar' : 'Definitions',
            icon: ListBulletIcon,
            path: '/camasirhane/definitions',
            description: language === 'tr' ? 'Departman ve malzeme cinsleri' : 'Departments and materials',
          },
          {
            label: language === 'tr' ? 'Modül Erişimi' : 'Module Access',
            icon: UserGroupIcon,
            path: '/camasirhane/access',
            description: language === 'tr' ? 'Çamaşırhane modül yetkileri' : 'Laundry module permissions',
          },
        ] : []),
      ],
    },
  ];

  const internalAuditMenuItems = [
    ...(hasModuleAccess('doc_convert') ? [{
      label: language === 'tr' ? 'Word → Excel' : 'Word → Excel',
      icon: ArrowsRightLeftIcon,
      path: '/internal-audit/doc-convert',
      description: language === 'tr' ? 'Word notalarını Excel tablosuna dönüştür' : 'Convert Word notes to Excel',
      subItems: [
        {
          label: language === 'tr' ? 'Dashboard' : 'Dashboard',
          icon: ChartPieIcon,
          path: '/internal-audit/doc-convert/dashboard',
          description: language === 'tr' ? 'Not istatistikleri ve grafikler' : 'Note statistics and charts'
        },
        {
          label: language === 'tr' ? 'Panom' : 'My Notes',
          icon: UserGroupIcon,
          path: '/internal-audit/doc-convert/my-notes',
          description: language === 'tr' ? 'Bana atanan notlar ve dönüş durumum' : 'Notes assigned to me and my returns'
        },
        {
          label: language === 'tr' ? 'Dönüştür' : 'Convert',
          icon: ArrowsRightLeftIcon,
          path: '/internal-audit/doc-convert',
          description: language === 'tr' ? 'Word dosyası yükle ve Excel al' : 'Upload Word and export Excel'
        },
        {
          label: language === 'tr' ? 'Günlük Notlar' : 'Daily Notes',
          icon: DocumentTextIcon,
          path: '/internal-audit/doc-convert/notes',
          description: language === 'tr' ? 'Notları grid ekranında yönet' : 'Manage notes in grid view'
        },
        {
          label: language === 'tr' ? 'Klasörler' : 'Folders',
          icon: FolderIcon,
          path: '/internal-audit/doc-convert/folders',
          description: language === 'tr' ? 'Notları klasörlerde düzenle' : 'Organize notes in folders'
        },
        ...(isAdmin() ? [{
          label: language === 'tr' ? 'Erişim Yönetimi' : 'Access Management',
          icon: ShieldCheckIcon,
          path: '/internal-audit/doc-convert/access',
          description: language === 'tr' ? 'Word→Excel modül yetkileri' : 'Word→Excel module permissions'
        }] : []),
      ]
    }] : []),
    ...(hasModuleAccess('transcription') ? [{
      label: language === 'tr' ? 'Ses → Metin' : 'Speech to Text',
      icon: MicrophoneIcon,
      path: '/internal-audit/transcription',
      description: language === 'tr' ? 'Ses kayıtlarını metne dök' : 'Transcribe audio recordings',
      subItems: [
        {
          label: language === 'tr' ? 'Transkripsiyon' : 'Transcription',
          icon: MicrophoneIcon,
          path: '/internal-audit/transcription',
          description: language === 'tr' ? 'Ses yükle veya kaydet' : 'Upload or record audio'
        },
        ...(isAdmin() ? [{
          label: language === 'tr' ? 'Erişim Yönetimi' : 'Access Management',
          icon: ShieldCheckIcon,
          path: '/internal-audit/transcription/access',
          description: language === 'tr' ? 'Ses→Metin modül yetkileri' : 'Transcription module permissions'
        }] : []),
      ]
    }] : []),
    ...(hasModuleAccess('technical_maintenance') ? [{
      label: language === 'tr' ? 'Teknik Bakım' : 'Technical Maintenance',
      icon: WrenchScrewdriverIcon,
      path: '/internal-audit/technical-maintenance',
      description: language === 'tr' ? 'Makine envanteri, bakım ve fatura takibi' : 'Equipment inventory, maintenance and invoices',
      subItems: [
        {
          label: language === 'tr' ? 'Özet' : 'Dashboard',
          icon: ChartBarIcon,
          path: '/internal-audit/technical-maintenance',
          description: language === 'tr' ? 'Teknik bakım özeti' : 'Technical maintenance summary',
        },
        {
          label: language === 'tr' ? 'Ekipman Envanteri' : 'Equipment',
          icon: ClipboardDocumentListIcon,
          path: '/internal-audit/technical-maintenance/equipment',
          description: language === 'tr' ? 'Makine, soğuk oda, dolap, buz makinesi' : 'Machines, cold rooms, cabinets, ice machines',
        },
        {
          label: language === 'tr' ? 'Bakım Kayıtları' : 'Maintenance',
          icon: WrenchScrewdriverIcon,
          path: '/internal-audit/technical-maintenance/maintenance',
          description: language === 'tr' ? 'Bakım ve servis geçmişi' : 'Maintenance history',
        },
        {
          label: language === 'tr' ? 'Bakım Takvimi' : 'Maintenance Schedule',
          icon: CalendarIcon,
          path: '/internal-audit/technical-maintenance/schedule',
          description: language === 'tr' ? 'Geciken ve yaklaşan bakımlar' : 'Overdue and upcoming maintenance',
        },
        {
          label: language === 'tr' ? 'Raporlar' : 'Reports',
          icon: DocumentChartBarIcon,
          path: '/internal-audit/technical-maintenance/reports',
          description: language === 'tr' ? 'Maliyet özeti, garanti takibi, CSV export' : 'Cost summary, warranty tracking, CSV export',
        },
        {
          label: language === 'tr' ? 'Fatura Girişi' : 'Invoices',
          icon: CurrencyDollarIcon,
          path: '/internal-audit/technical-maintenance/invoices',
          description: language === 'tr' ? 'Bakım ve servis faturaları' : 'Maintenance invoices',
        },
        {
          label: language === 'tr' ? 'İşlem Geçmişi' : 'Activity History',
          icon: ClockIcon,
          path: '/internal-audit/technical-maintenance/activity',
          description: language === 'tr' ? 'Ekipman, bakım ve fatura değişiklikleri' : 'Equipment, maintenance and invoice changes',
        },
        ...(isAdmin() ? [{
          label: language === 'tr' ? 'Erişim Yönetimi' : 'Access Management',
          icon: ShieldCheckIcon,
          path: '/internal-audit/technical-maintenance/access',
          description: language === 'tr' ? 'Teknik bakım modül yetkileri' : 'Technical maintenance permissions',
        }] : []),
      ],
    }] : []),
    ...(isAdmin() ? [{
      label: language === 'tr' ? 'Modül Yetkilendirme' : 'Module Access',
      icon: UserGroupIcon,
      path: '/internal-audit/access-management',
      description: language === 'tr' ? 'Denetim modülü kullanıcı yetkileri' : 'Internal audit module permissions',
    }] : []),
  ];

  const foodBeverageMenuItems = [
    {
      label: 'Food and Beverage',
      icon: ClipboardDocumentListIcon,
      path: '/food-beverage/hotels',
      description: 'QR menü ayarları ve ürün yönetimi',
      subItems: [
        {
          label: 'QR Oteller',
          icon: HomeIcon,
          path: '/food-beverage/hotels',
          description: 'qr.akkahotels.com/x yapısı için otel tanımları'
        },
        {
          label: 'Ürün Grupları',
          icon: ListBulletIcon,
          path: '/food-beverage/product-groups',
          description: 'Menü ürün grup yönetimi'
        },
        {
          label: 'Alanlar',
          icon: TableCellsIcon,
          path: '/food-beverage/areas',
          description: 'Menü alan/seksiyon yönetimi'
        },
        {
          label: 'Ürünler',
          icon: DocumentTextIcon,
          path: '/food-beverage/products',
          description: 'Ürün içerik ve fiyat yönetimi'
        }
      ]
    }
  ];



  const bottomMenuItems = [
    {
      label: t('team'),
      icon: UserGroupIcon,
      path: '/team',
      description: language === 'tr' ? 'Takım üyeleri' : 'Team members'
    },
    ...(canAccessWorkReport() ? [{
      label: language === 'tr' ? 'Rapor Alma' : 'Work Report',
      icon: DocumentChartBarIcon,
      path: '/reports',
      description: language === 'tr' ? 'İş yapanlar Excel raporu' : 'Work report Excel export'
    }] : []),
  ];


  const isActive = (path) => location.pathname === path;

  // Check if current path is StopSale or HelpDesk
  // URL bazlı kontrol VEYA seçili projeye göre modül algısı
  // Modül-bağımsız sayfalar için (settings, team, profile) proje context'ini koru
  const moduleIndependentPages = ['/settings', '/team', '/profile', '/reports'];
  const isModuleIndependentPage = moduleIndependentPages.includes(location.pathname);

  const normalizedProjectName = selectedProject?.project_name
    ? selectedProject.project_name.toLowerCase()
    : '';
  const isSalesMarketingProject = selectedProject?.project_code === 'SM' ||
    (normalizedProjectName.includes('sales') && normalizedProjectName.includes('marketing'));



  const isStopSaleModule = location.pathname.startsWith('/stopsale') ||
    location.pathname.startsWith('/marketing-budget') ||
    location.pathname.startsWith('/content-management') ||
    location.pathname.startsWith('/marketing-management') ||
    location.pathname.startsWith('/sales-call') ||
    location.pathname.startsWith('/survey') ||
    isSalesMarketingProject ||
    // Modül-bağımsız sayfalarda son seçili modülü koru
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'stopsale');

  const isQualityModule = location.pathname.startsWith('/quality-management') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'quality-management');

  const isFoodBeverageModule = location.pathname.startsWith('/food-beverage') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'food-beverage');

  const isGeneralModule = location.pathname.startsWith('/general') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'general');

  const isInternalAuditModule = location.pathname.startsWith('/internal-audit') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'internal-audit');

  const isVehicleMaintenanceModule = location.pathname.startsWith('/vehicle-maintenance') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'vehicle-maintenance');

  const isCamasirhaneModule = location.pathname.startsWith('/camasirhane') ||
    (isModuleIndependentPage && localStorage.getItem('lastActiveModule') === 'camasirhane');

  const isHelpDeskModule = !isStopSaleModule && !isQualityModule && !isFoodBeverageModule
    && !isGeneralModule && !isInternalAuditModule && !isVehicleMaintenanceModule && !isCamasirhaneModule;

  // Otomatik olarak aktif alt menüye sahip menüleri aç
  useEffect(() => {
    const currentMenuItems = isCamasirhaneModule
      ? camasirhaneMenuItems
      : isVehicleMaintenanceModule
      ? vehicleMaintenanceMenuItems
      : isInternalAuditModule
      ? internalAuditMenuItems
      : isGeneralModule
      ? generalMenuItems
      : isFoodBeverageModule
      ? foodBeverageMenuItems
      : isQualityModule
      ? qualityMenuItems
      : (isStopSaleModule ? stopSaleMenuItems : menuItems);
    setOpenSubMenus(prev => {
      const newOpenSubMenus = { ...prev };
      let hasChanges = false;

      currentMenuItems.forEach((item, index) => {
        if (item.subItems && item.subItems.length > 0) {
          const menuKey = item.path || `menu-${index}`;
          const hasActiveSub = item.subItems.some(subItem => isActive(subItem.path));
          if (hasActiveSub && !newOpenSubMenus[menuKey]) {
            newOpenSubMenus[menuKey] = true;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newOpenSubMenus : prev;
    });
  }, [location.pathname, isStopSaleModule, isQualityModule, isFoodBeverageModule, isGeneralModule, isInternalAuditModule, isVehicleMaintenanceModule, isCamasirhaneModule]);

  const handleNavigate = (path) => {
    // Settings, team, profile gibi modül-bağımsız sayfalar için özel handling
    const moduleIndependentPages = ['/settings', '/team', '/profile', '/reports'];

    // Modül-bağımsız sayfalara gitmeden önce mevcut modülü kaydet
    if (moduleIndependentPages.includes(path)) {
      let currentModule = 'helpdesk';
      if (isStopSaleModule) currentModule = 'stopsale';
      if (isQualityModule) currentModule = 'quality-management';
      if (isFoodBeverageModule) currentModule = 'food-beverage';
      if (isGeneralModule) currentModule = 'general';
      if (isInternalAuditModule) currentModule = 'internal-audit';
      if (isVehicleMaintenanceModule) currentModule = 'vehicle-maintenance';
      if (isCamasirhaneModule) currentModule = 'camasirhane';
      localStorage.setItem('lastActiveModule', currentModule);
    }

    navigate(path);
    if (onCloseMobile) onCloseMobile();
  };

  const handleProjectsClick = async () => {
    setClickCount(prev => prev + 1);

    // Tek tıklama - dropdown aç/kapat ve projeleri yükle
    if (clickCount === 0) {
      // Projeler henüz yüklenmemişse yükle
      if (!projects || !Array.isArray(projects) || projects.length === 0) {
        // console.log('📁 Projects not loaded, loading now...');
        await reloadProjects();
      }
      setIsProjectsOpen(!isProjectsOpen);
      setTimeout(() => setClickCount(0), 300); // 300ms sonra reset
    }
    // Çift tıklama - Projects sayfasına git
    else if (clickCount === 1) {
      navigate('/projects');
      setClickCount(0);
    }
  };

  const handleProjectSelect = (project) => {
    selectProject(project);
    if (onCloseMobile) onCloseMobile();
    // Dropdown'u açık bırak - kapanmasın
    // setIsProjectsOpen(false); // Bu satırı kaldırdık
  };


  const getProjectIcon = (project) => {
    // Eğer proje objesi icon alanına sahipse onu kullan
    if (project && project.project_icon) {
      // Gerçek icon dosyası varsa onu göster
      if (project.project_icon.startsWith('icons8-')) {
        return (
          <img
            src={`/project-icons/${project.project_icon}.png`}
            alt={project.project_icon}
            className="w-4 h-4 object-contain"
          />
        );
      }
    }

    // Varsayılan icon
    return '📁';
  };

  return (
    <aside className={`${isCollapsed ? 'w-64 md:w-16' : 'w-64'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full transition-all duration-300 z-30 fixed md:relative inset-y-0 left-0 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
      {/* Logo removed as it is now in the top Header */}

      {/* Quick Actions */}
      <div className={`${isCollapsed ? 'px-2' : 'px-4'} py-4 border-b border-gray-200 dark:border-gray-700 space-y-2`}>
        {/* Back to Module Selection */}
        <button
          onClick={() => navigate('/module-selection')}
          className={`w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${isCollapsed ? 'p-2' : 'px-4 py-2'} rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center ${isCollapsed ? 'justify-center' : 'justify-center space-x-2'} text-sm font-medium`}
          title={isCollapsed ? (language === 'tr' ? 'Modül Seçimi' : 'Module Selection') : ''}
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {!isCollapsed && (
            <span className="font-dynamic-text">
              {language === 'tr' ? 'Modül Seçimi' : 'Module Selection'}
            </span>
          )}
        </button>

        {/* Create Ticket Button - only show in HelpDesk module */}
        {isHelpDeskModule && (
          <button
            onClick={onCreateTicket}
            className={`w-full bg-blue-600 text-white ${isCollapsed ? 'p-2' : 'px-3 py-1.5'} rounded-lg hover:bg-blue-700 transition-colors flex items-center ${isCollapsed ? 'justify-center' : 'justify-start space-x-2'} text-xs font-semibold`}
            title={isCollapsed ? t('createTicket') : ''}
          >
            <PlusIcon className="w-3.5 h-3.5" />
            {!isCollapsed && <span className="font-dynamic-text text-left truncate leading-tight">{t('createTicket')}</span>}
          </button>
        )}

      </div>

      {/* Main Navigation */}
      <nav className={`flex-1 ${isCollapsed ? 'px-2' : 'px-4'} py-4 space-y-1 overflow-y-auto sidebar-scroll`}>
        {!isCollapsed && (
          <div className="font-dynamic-text text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-3">
            {isCamasirhaneModule
              ? (language === 'tr' ? 'ÇAMAŞIRHANE' : 'LAUNDRY')
              : isVehicleMaintenanceModule
              ? (language === 'tr' ? 'ARAÇ BAKIM' : 'VEHICLE MAINTENANCE')
              : isGeneralModule
              ? (language === 'tr' ? 'GENEL' : 'GENERAL')
              : isInternalAuditModule
              ? (language === 'tr' ? 'DENETİM' : 'INTERNAL AUDIT')
              : isStopSaleModule
              ? (language === 'tr' ? 'MENU' : 'MENU')
              : isFoodBeverageModule
                ? 'FOOD & BEVERAGE'
              : isQualityModule
                ? 'KALİTE'
              : (language === 'tr' ? 'HELPDESK' : 'HELPDESK')
            }
          </div>
        )}

        {/* Projects Dropdown - only show in HelpDesk module - MOVED TO TOP */}
        {!isCollapsed && isHelpDeskModule && (
          <div className="space-y-1 mb-4">
            <button
              onClick={handleProjectsClick}
              className={`w-full flex items-center justify-start px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors group ${isActive('/projects')
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-gray-200 border-l-2 border-blue-700 dark:border-blue-400'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              title="Proje yönetimi (Çift tıklayın) / Proje seçimi (Tek tıklayın)"
            >
              <FolderIcon className={`w-4 h-4 mr-2.5 ${isActive('/projects') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
              <span className="font-dynamic-text text-left truncate leading-tight">{t('projects')}</span>

              {/* Dropdown arrow */}
              <div className="ml-auto flex items-center space-x-2">
                {isProjectsOpen ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4" />
                )}
              </div>
            </button>

            {/* Projects List */}
            {isProjectsOpen && (
              <div className="ml-8 space-y-1">
                {projects && Array.isArray(projects) && projects.filter(project => project && project.id).map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectSelect(project)}
                    className={`w-full flex items-center justify-start px-2.5 py-1 rounded-md text-xs font-medium transition-colors group ${selectedProject?.id === project.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    title={project.description}
                  >
                    <span className="mr-2 flex items-center justify-center w-3.5 h-3.5">
                      {getProjectIcon(project)}
                    </span>
                    <span className="font-dynamic-text truncate text-left leading-tight">{project.project_name}</span>
                  </button>
                ))}

                {/* Create Project Button */}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                  <button
                    onClick={() => navigate('/projects/create')}
                    className="w-full flex items-center justify-start px-2.5 py-1 rounded-md text-xs font-medium transition-colors group text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300"
                    title={language === 'tr' ? 'Yeni proje oluştur' : 'Create new project'}
                  >
                    <PlusIcon className="w-3.5 h-3.5 mr-2" />
                    <span className="font-dynamic-text text-left truncate leading-tight">
                      {language === 'tr' ? 'Proje Oluştur' : 'Create Project'}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Render appropriate menu based on module */}
        {(isCamasirhaneModule
          ? camasirhaneMenuItems
          : isVehicleMaintenanceModule
          ? vehicleMaintenanceMenuItems
          : isInternalAuditModule
          ? internalAuditMenuItems
          : isGeneralModule
          ? generalMenuItems
          : isFoodBeverageModule
          ? foodBeverageMenuItems
          : (isQualityModule ? qualityMenuItems : (isStopSaleModule ? stopSaleMenuItems : menuItems))).map((item, index) => {
          const Icon = item.icon;
          const hasSubItems = item.subItems && item.subItems.length > 0;
          // Ana menü item'ı sadece tam eşleşmede veya alt menüsü yoksa ve path başlıyorsa aktif olsun
          // Ancak alt menüsü varsa, sadece alt menü kapalıyken veya kendi path'i tam eşleşiyorsa aktif stilini KULLANMAMALI
          // Sadece subItem'lardan biri aktifse parent'ı aktif GÖSTERMEMELİ, sadece açık göstermeli.
          // İsteğe göre: "sadece açık olan pagein yada tıkladığımızın öyle olması gerekli"
          // Fix: Eğer bir alt menü öğesi parent ile aynı yola sahipse, parent'ı aktif yapma
          const menuKey = item.path || `menu-${index}`; // Path yoksa index kullan
          const active =
            item.path &&
            (item.path.startsWith('/mail')
              ? location.pathname.startsWith('/mail')
              : isActive(item.path)) &&
            (!hasSubItems || !item.subItems.some((sub) => sub.path === item.path));

          const isSubMenuOpen = openSubMenus[menuKey];
          const hasActiveSubItem = hasSubItems && item.subItems.some(subItem => isActive(subItem.path));

          return (
            <div key={menuKey} className="space-y-1">
              {/* Main menu item */}
              <button
                onClick={() => {
                  let currentModule = 'helpdesk';
                  if (isStopSaleModule) currentModule = 'stopsale';
                  if (isQualityModule) currentModule = 'quality-management';
                  if (isFoodBeverageModule) currentModule = 'food-beverage';
                  if (isGeneralModule) currentModule = 'general';
                  if (isInternalAuditModule) currentModule = 'internal-audit';
                  if (isVehicleMaintenanceModule) currentModule = 'vehicle-maintenance';
                  if (isCamasirhaneModule) currentModule = 'camasirhane';
                  localStorage.setItem('lastActiveModule', currentModule);

                  if (hasSubItems) {
                    const currentlyOpen = openSubMenus[menuKey];
                    setOpenSubMenus((prev) => ({
                      ...prev,
                      [menuKey]: !currentlyOpen,
                    }));
                    if (item.path) {
                      handleNavigate(item.path);
                    }
                  } else if (item.path) {
                    handleNavigate(item.path);
                  }
                }}
                className={`w-full flex items-center min-w-0 ${isCollapsed ? 'justify-center px-2' : 'px-2.5 justify-start'} py-1.5 rounded-lg text-xs font-semibold transition-colors group ${active
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-gray-200 border-l-2 border-blue-700 dark:border-blue-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                title={isCollapsed ? item.label : item.description}
              >
                <Icon className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'} flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
                {!isCollapsed && <span className={`font-dynamic-text truncate flex-1 min-w-0 text-left leading-tight ${item.className || ''}`}>{item.label}</span>}

                {/* Sub menu arrow */}
                {hasSubItems && !isCollapsed && (
                  <div className="ml-auto">
                    {isSubMenuOpen ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                  </div>
                )}

              </button>

              {/* Sub menu items */}
              {hasSubItems && isSubMenuOpen && !isCollapsed && (
                <div className="ml-5 space-y-0.5 border-l-2 border-blue-200 dark:border-blue-800 pl-2.5">
                  {item.subItems.map((subItem) => {
                    const SubIcon = subItem.icon || DocumentTextIcon; // Fallback icon
                    const subActive = isActive(subItem.path);
                    const hasSubSubItems = subItem.subItems && subItem.subItems.length > 0;
                    const isSubSubMenuOpen = openSubMenus[subItem.path];
                    const hasActiveSubSubItem = hasSubSubItems && subItem.subItems.some(subSubItem => isActive(subSubItem.path));

                    return (
                      <div key={subItem.path} className="space-y-1">
                        <button
                          onClick={() => {
                            if (hasSubSubItems) {
                              // Toggle sub-sub menu
                              setOpenSubMenus(prev => ({
                                ...prev,
                                [subItem.path]: !prev[subItem.path]
                              }));
                              return;
                            }
                            let currentModule = 'helpdesk';
                            if (isStopSaleModule) currentModule = 'stopsale';
                            if (isQualityModule) currentModule = 'quality-management';
                            if (isFoodBeverageModule) currentModule = 'food-beverage';
                            if (isGeneralModule) currentModule = 'general';
                            if (isInternalAuditModule) currentModule = 'internal-audit';
                  if (isVehicleMaintenanceModule) currentModule = 'vehicle-maintenance';
                  if (isCamasirhaneModule) currentModule = 'camasirhane';
                            localStorage.setItem('lastActiveModule', currentModule);
                            handleNavigate(subItem.path);
                          }}
                          className={`w-full flex items-center min-w-0 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors group justify-start ${subActive
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          title={subItem.description}
                        >
                          <SubIcon className={`w-3.5 h-3.5 mr-2.5 flex-shrink-0 ${subActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                          <span className="font-dynamic-text truncate flex-1 min-w-0 text-left leading-tight">{subItem.label}</span>
                          {/* Sub-sub menu arrow */}
                          {hasSubSubItems && (
                            <div className="ml-auto">
                              {isSubSubMenuOpen ? (
                                <ChevronDownIcon className="w-3 h-3" />
                              ) : (
                                <ChevronRightIcon className="w-3 h-3" />
                              )}
                            </div>
                          )}
                        </button>
                        {/* Sub-sub menu items */}
                        {hasSubSubItems && isSubSubMenuOpen && (
                          <div className="ml-5 space-y-0.5 border-l-2 border-blue-200 dark:border-blue-800 pl-2.5">
                            {subItem.subItems.map((subSubItem) => {
                              const SubSubIcon = subSubItem.icon || DocumentTextIcon;
                              const subSubActive = isActive(subSubItem.path);

                              return (
                                <button
                                  key={subSubItem.path}
                                  onClick={() => {
                                    let currentModule = 'helpdesk';
                                    if (isStopSaleModule) currentModule = 'stopsale';
                                    if (isQualityModule) currentModule = 'quality-management';
                                    if (isFoodBeverageModule) currentModule = 'food-beverage';
                                    if (isGeneralModule) currentModule = 'general';
                                    if (isInternalAuditModule) currentModule = 'internal-audit';
                  if (isVehicleMaintenanceModule) currentModule = 'vehicle-maintenance';
                  if (isCamasirhaneModule) currentModule = 'camasirhane';
                                    localStorage.setItem('lastActiveModule', currentModule);
                                    handleNavigate(subSubItem.path);
                                  }}
                                  className={`w-full flex items-center px-2.5 py-1 rounded-lg text-xs font-medium transition-colors group justify-start ${subSubActive
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                  title={subSubItem.description}
                                >
                                  <SubSubIcon className={`w-3 h-3 mr-2 flex-shrink-0 ${subSubActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                  <span className="font-dynamic-text truncate text-left leading-tight">{subSubItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom Menu Items - Help Desk only */}
        {isHelpDeskModule && (
        <>
          {/* Separator */}
          {!isCollapsed && <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>}

          {!isCollapsed && (
            <div className="font-dynamic-text text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-3">
              {language === 'tr' ? 'DİĞER' : 'MORE'}
            </div>
          )}

          {bottomMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`w-full flex items-center min-w-0 ${isCollapsed ? 'justify-center px-2' : 'px-2.5 justify-start'} py-1.5 rounded-lg text-xs font-semibold transition-colors group ${active
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-gray-200 border-l-2 border-blue-700 dark:border-blue-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                title={isCollapsed ? item.label : item.description}
              >
                <Icon className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2.5'} flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
                {!isCollapsed && <span className="font-dynamic-text truncate flex-1 min-w-0 text-left leading-tight">{item.label}</span>}
              </button>
            );
          })}
        </>
        )}

      </nav>


    </aside>
  );
};

export default Sidebar;